import { format } from 'date-fns';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { zonedEvent } from '@/lib/timezone';

import type { MemoryListItem } from '../api/use-memories';
import type { MemoryScopeOption } from '../api/use-memory-scopes';

export interface MemoryEditDialogProps {
  memory: MemoryListItem | null;
  /** The available skill scopes (Phase 3), from `useMemoryScopes`. */
  scopeOptions: MemoryScopeOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (input: {
    id: string;
    content: string;
    /** `YYYY-MM-DD` to set an expiry, `null` to clear it. */
    expiresAt?: string | null;
    /** `null`/`[]` for "applies everywhere". */
    scopeSkillIds?: string[] | null;
  }) => void;
  /** The business timezone the expiry date is read and written in. */
  timeZone: string;
  isSaving?: boolean;
}

const MAX_CONTENT_LENGTH = 2000;

/** ISO instant → the `YYYY-MM-DD` a date input wants, read in the BUSINESS
 *  zone. An expiry is 23:59 there, so reading it in the viewer's zone would
 *  show — and then save back — the wrong day. */
const toDateInputValue = (iso: string | null, timeZone: string): string =>
  iso ? format(zonedEvent(iso, timeZone), 'yyyy-MM-dd') : '';

/** Today in the business zone, as the input's `min` — a past expiry is
 *  refused server-side, so don't offer it. */
const todayValue = (timeZone: string): string =>
  format(zonedEvent(new Date().toISOString(), timeZone), 'yyyy-MM-dd');

/**
 * Edit dialog for a single memory — its wording and, when it should stop
 * applying. Backed by `useEditMemory`.
 *
 * The expiry is sent as a plain calendar date and resolved to an instant
 * SERVER-side against the org timezone, so the browser's zone never decides
 * what "the 10th" means. Clearing the field makes the memory permanent
 * again — the escape hatch when Claire dated one from a chat phrase and got
 * it wrong.
 *
 * The dialog stays mounted while the edit is in flight (no spinner — the
 * Save button shows "Saving..." instead, mirroring the rest of the app).
 * On a successful save, the parent closes via `onOpenChange(false)`.
 *
 * Hard-block 400 errors come back from the server with a human-readable
 * message; the parent surfaces it via `toast.error` (handled in the hook).
 */
export function MemoryEditDialog({
  memory,
  scopeOptions,
  open,
  onOpenChange,
  onSave,
  timeZone,
  isSaving = false,
}: MemoryEditDialogProps) {
  const [content, setContent] = useState('');
  const [expiry, setExpiry] = useState('');
  const [scopeSkillIds, setScopeSkillIds] = useState<string[]>([]);

  // Reset the fields when the dialog opens with a new memory; not when
  // it closes (avoids the textarea blanking out mid-fade).
  useEffect(() => {
    if (open && memory) {
      setContent(memory.content);
      setExpiry(toDateInputValue(memory.expiresAt, timeZone));
      setScopeSkillIds(memory.scopeSkillIds ?? []);
    }
  }, [open, memory, timeZone]);

  const originalExpiry = memory
    ? toDateInputValue(memory.expiresAt, timeZone)
    : '';
  const originalScopeSkillIds = memory?.scopeSkillIds ?? [];
  const isScopeUnchanged =
    scopeSkillIds.length === originalScopeSkillIds.length &&
    scopeSkillIds.every((id) => originalScopeSkillIds.includes(id));
  const isUnchanged = memory
    ? content.trim() === memory.content.trim() &&
      expiry === originalExpiry &&
      isScopeUnchanged
    : true;
  const isExpiryInPast = expiry !== '' && expiry < todayValue(timeZone);
  const isInvalid =
    !content.trim() || content.length > MAX_CONTENT_LENGTH || isExpiryInPast;

  const toggleScope = (id: string, checked: boolean) => {
    setScopeSkillIds((prev) =>
      checked ? [...prev, id] : prev.filter((s) => s !== id)
    );
  };

  const handleSave = () => {
    if (!memory || isInvalid || isSaving) return;
    onSave({
      id: memory.id,
      content: content.trim(),
      // Only send the expiry when it actually changed: omitted means "leave
      // it as it is", which is a different edit from clearing it.
      ...(expiry === originalExpiry
        ? {}
        : { expiresAt: expiry === '' ? null : expiry }),
      // Same "only send if changed" discipline for scope.
      ...(isScopeUnchanged
        ? {}
        : { scopeSkillIds: scopeSkillIds.length > 0 ? scopeSkillIds : null }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit memory</DialogTitle>
          <DialogDescription>
            Tweak what Claire remembers, and when she should stop using it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What should Claire remember?"
            rows={5}
            maxLength={MAX_CONTENT_LENGTH}
            aria-invalid={isInvalid}
            disabled={isSaving}
          />
          <p className="text-muted-foreground text-xs">
            {content.length}/{MAX_CONTENT_LENGTH}
          </p>
        </div>

        {scopeOptions.length > 0 && (
          <div className="space-y-2">
            <Label>Applies to</Label>
            <p className="text-muted-foreground text-xs">
              Leave everything unchecked to apply this everywhere Claire
              generates. Check one or more to limit it to just those.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {scopeOptions.map((option) => (
                <div key={option.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`memory-scope-${option.id}`}
                    checked={scopeSkillIds.includes(option.id)}
                    onCheckedChange={(checked) =>
                      toggleScope(option.id, checked === true)
                    }
                    disabled={isSaving}
                  />
                  <Label
                    htmlFor={`memory-scope-${option.id}`}
                    className="font-normal"
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="memory-expiry">Stop using this after</Label>
          <div className="flex items-center gap-2">
            <Input
              id="memory-expiry"
              type="date"
              className="w-auto"
              value={expiry}
              min={todayValue(timeZone)}
              onChange={(e) => setExpiry(e.target.value)}
              aria-invalid={isExpiryInPast}
              disabled={isSaving}
            />
            {expiry !== '' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpiry('')}
                disabled={isSaving}
              >
                Clear
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-xs">
            {isExpiryInPast
              ? 'Pick a date in the future, or clear it.'
              : expiry === ''
                ? 'Leave empty and Claire keeps using this until you delete it.'
                : 'Claire drops this memory by herself once the date passes.'}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isInvalid || isUnchanged || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
